// The map: our own LA basemap (Protomaps in R2), with every curb that works for your stay drawn
// in its colour, numbered pins for the loop, the garage, and live meter sensors.
// MapLibre (~1 MB) loads only when a map is first shown, so the home screen stays instant.

import { decodePolyline } from '../src/shared/grid.ts'
import grid from '../src/shared/grid.json' with { type: 'json' }

/* global __MAPLIBRE__ */
const MAPLIBRE = __MAPLIBRE__ // injected by scripts/build-client.mjs, so an upgrade busts the browser cache

let libP = null
function lib() {
  libP ??= (async () => {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = `/vendor/maplibre/maplibre-gl.css?v=${MAPLIBRE}`
    document.head.append(link)
    return import(`/vendor/maplibre/maplibre-gl.mjs?v=${MAPLIBRE}`)
  })()
  libP.catch(() => { libP = null })
  return libP
}

let styleP = null
async function style() {
  styleP ??= fetch('/map/style.json').then((r) => r.json()).then((s) => {
    // MapLibre wants absolute URLs.
    s.glyphs = location.origin + s.glyphs
    s.sources.protomaps.tiles = s.sources.protomaps.tiles.map((t) => location.origin + t)
    return s
  })
  styleP.catch(() => { styleP = null })
  return structuredClone(await styleP)
}

/** Warm the cache while the home screen idles: MapLibre, its worker and our style. */
export function preload() {
  return Promise.all([lib(), style()]).catch(() => {})
}

export const COLORS = {
  yellow: '#ffd23f', white: '#fff3d1', go: '#7fcf5a', pay: '#7cc4ff', short: 'rgba(255, 255, 223, 0.28)',
}

export const kindColor = (kind) => ({ yellow: COLORS.yellow, white: COLORS.white, meter: COLORS.pay }[kind] ?? COLORS.go)

const lineColor = ['case',
  ['==', ['get', 'status'], 'short'], COLORS.short,
  ['==', ['get', 'kind'], 'yellow'], COLORS.yellow,
  ['==', ['get', 'kind'], 'white'], COLORS.white,
  ['==', ['get', 'status'], 'pay'], COLORS.pay,
  COLORS.go,
]
const width = (a, b, c) => ['interpolate', ['linear'], ['zoom'], 14, a, 17, b, 19.5, c]
// The loop's own curbs draw twice as thick as everything else that works.
const candWidth = (a, b, c) => ['interpolate', ['linear'], ['zoom'], 14, ['*', a, ['+', 1, ['*', 1.5, ['get', 'cand']]]], 17, ['*', b, ['+', 1, ['*', 1.5, ['get', 'cand']]]], 19.5, ['*', c, ['+', 1, ['*', 1.5, ['get', 'cand']]]]]
const candOpacity = ['case', ['==', ['get', 'cand'], 1], 1, 0.55]

const empty = () => ({ type: 'FeatureCollection', features: [] })

export async function createMap(el, { center, zoom = 15.5 }) {
  const ml = await lib()
  const map = new ml.Map({
    container: el, style: await style(), center: [center.lon, center.lat], zoom,
    minZoom: 10, maxZoom: 19.5, maxBounds: [[-118.95, 33.5], [-117.8, 34.55]],
    attributionControl: { compact: true }, dragRotate: false, pitchWithRotate: false, touchPitch: false,
    cooperativeGestures: false, fadeDuration: 0,
  })
  map.touchZoomRotate.disableRotation()
  map.keyboard.disableRotation()
  // Tile or glyph hiccups raise 'error' but don't stop the map, so only a missing 'load' is fatal.
  map.on('error', (e) => console.warn('map:', e.error?.message ?? e.error, e.sourceId ?? '', e.tile?.tileID?.canonical ?? ''))
  await new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('map did not load')), 20000)
    map.once('load', () => { clearTimeout(t); resolve() })
  })
  map.addSource('heat', { type: 'geojson', data: empty() })
  map.addLayer({ id: 'heat', type: 'fill', source: 'heat', layout: { visibility: 'none' }, paint: {
    'fill-color': ['interpolate', ['linear'], ['get', 'p'], 0.02, 'rgba(255,210,63,0.10)', 0.15, 'rgba(255,170,60,0.28)', 0.35, 'rgba(255,110,60,0.42)', 0.6, 'rgba(255,70,60,0.55)'],
    'fill-outline-color': 'rgba(0,0,0,0)',
  } })
  map.addSource('curbs', { type: 'geojson', data: empty() })
  map.addSource('focus', { type: 'geojson', data: empty() })
  map.addSource('live', { type: 'geojson', data: empty() })
  map.addLayer({ id: 'curbs-casing', type: 'line', source: 'curbs', filter: ['!=', ['get', 'status'], 'short'], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#140904', 'line-width': candWidth(3, 7.5, 14), 'line-opacity': 0.85 } })
  map.addLayer({ id: 'curbs-glow', type: 'line', source: 'curbs', filter: ['all', ['==', ['get', 'cand'], 1], ['==', ['get', 'kind'], 'yellow']], layout: { 'line-cap': 'round' }, paint: { 'line-color': COLORS.yellow, 'line-width': width(10, 18, 30), 'line-opacity': 0.35, 'line-blur': 6 } })
  map.addLayer({ id: 'curbs-line', type: 'line', source: 'curbs', filter: ['!=', ['get', 'status'], 'wait'], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': lineColor, 'line-width': candWidth(1.6, 4.5, 9), 'line-opacity': candOpacity } })
  map.addLayer({ id: 'curbs-wait', type: 'line', source: 'curbs', filter: ['==', ['get', 'status'], 'wait'], layout: { 'line-join': 'round' }, paint: { 'line-color': lineColor, 'line-width': width(1.6, 4.5, 9), 'line-dasharray': [1.2, 1] } })
  map.addLayer({ id: 'focus-glow', type: 'line', source: 'focus', layout: { 'line-cap': 'round' }, paint: { 'line-color': '#ffffdf', 'line-width': width(6, 12, 20), 'line-opacity': 0.35, 'line-blur': 2 } })
  map.addLayer({ id: 'focus-line', type: 'line', source: 'focus', layout: { 'line-cap': 'round' }, paint: { 'line-color': ['coalesce', ['get', 'color'], '#ffffdf'], 'line-width': width(2.5, 6, 11) } })
  // Open sensor spaces only: taken ones are most of them and just add noise.
  map.addLayer({ id: 'live', type: 'circle', source: 'live', minzoom: 15.5, filter: ['==', ['get', 'vacant'], 1], paint: {
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 15.5, 2.2, 18, 5],
    'circle-color': '#9be27a', 'circle-stroke-color': '#140904', 'circle-stroke-width': 1,
  } })
  map.addLayer({ id: 'curbs-hit', type: 'line', source: 'curbs', paint: { 'line-color': '#000', 'line-opacity': 0, 'line-width': 22 } })
  return { map, ml, markers: [] }
}

function lineFeature(geom, props) {
  const pts = decodePolyline(geom)
  const coords = pts.length === 1 ? [[pts[0][1], pts[0][0]], [pts[0][1] + 0.00003, pts[0][0]]] : pts.map(([la, lo]) => [lo, la])
  return { type: 'Feature', properties: props, geometry: { type: 'LineString', coordinates: coords } }
}

function marker(ctx, cls, html, at, onClick, label, anchor = 'center') {
  const el = document.createElement('button')
  el.type = 'button'
  el.className = `pin ${cls}`
  el.innerHTML = html
  if (label) el.setAttribute('aria-label', label)
  if (onClick) el.addEventListener('click', (e) => { e.stopPropagation(); onClick() })
  const m = new ctx.ml.Marker({ element: el, anchor }).setLngLat([at.lon, at.lat]).addTo(ctx.map)
  ctx.markers.push(m)
  return m
}

export function clearMarkers(ctx) {
  for (const m of ctx.markers) m.remove()
  ctx.markers = []
}

/** Draw a plan. Handlers get ids; returns nothing. */
export function showPlan(ctx, plan, { onCandidate, onCurb, onGarage, me } = {}) {
  const { map } = ctx
  const picked = new Set(plan.candidates.map((c) => c.id))
  const drawn = new Set(plan.map.curbs.map((x) => x[0]))
  map.getSource('curbs').setData({
    type: 'FeatureCollection',
    features: [
      ...plan.map.curbs.map(([id, status, kind, geom]) => lineFeature(geom, { id, status, kind, cand: picked.has(id) ? 1 : 0 })),
      // candidates outside the drawn radius still get their line
      ...plan.candidates.filter((c) => !drawn.has(c.id)).map((c) => lineFeature(c.geom, { id: c.id, status: c.cost > 0 ? 'pay' : 'go', kind: c.kind, cand: 1 })),
    ],
  })
  // Block-level ticket odds as squares (cell id → its 150 m cell on the shared grid).
  const { origin, cell } = grid
  map.getSource('heat').setData({
    type: 'FeatureCollection',
    features: (plan.map.heat ?? []).map(([id, p]) => {
      const r = Math.floor(id / cell.stride), c = id % cell.stride
      const la = origin.lat + r * cell.dlat, lo = origin.lon + c * cell.dlon
      return { type: 'Feature', properties: { p }, geometry: { type: 'Polygon', coordinates: [[[lo, la], [lo + cell.dlon, la], [lo + cell.dlon, la + cell.dlat], [lo, la + cell.dlat], [lo, la]]] } }
    }),
  })
  map.getSource('live').setData({
    type: 'FeatureCollection',
    features: plan.map.live.map(([lat, lon, vacant]) => ({ type: 'Feature', properties: { vacant }, geometry: { type: 'Point', coordinates: [lon, lat] } })),
  })
  clearMarkers(ctx)
  marker(ctx, 'pin-dest', '<span></span>', plan.at, null, 'Destination')
  if (me) marker(ctx, 'pin-me', '<span></span>', me, null, 'You')
  const g = plan.garages[0]
  if (g) marker(ctx, 'pin-garage', 'P', g, () => onGarage?.(g.id), `Garage: ${g.name}`)
  for (const c of plan.candidates) {
    // Stemmed pin: the tip sits on the curb so the stretch itself stays visible.
    // A meter that's free for the whole stay is green, like its curb line (blue means you'll pay).
    marker(ctx, `pin-n pin-${c.kind === 'meter' && c.cost === 0 ? 'free' : c.kind}`, `<span>${c.n}</span>`, c, () => onCandidate?.(c.id), `Spot ${c.n}: ${c.street}`, 'bottom')
  }
  if (onCurb && !ctx.wired) {
    ctx.wired = true
    map.on('click', 'curbs-hit', (e) => {
      const f = e.features?.[0]
      if (f) ctx.onCurb?.(f.properties.id)
    })
    map.on('mouseenter', 'curbs-hit', () => (map.getCanvas().style.cursor = 'pointer'))
    map.on('mouseleave', 'curbs-hit', () => (map.getCanvas().style.cursor = ''))
  }
  ctx.onCurb = onCurb
  fitPlan(ctx, plan, me)
}

export function fitPlan(ctx, plan, me, duration = 0) {
  const pts = [plan.at, ...plan.candidates, ...(plan.garages[0] ? [plan.garages[0]] : [])]
  if (me && Math.abs(me.lat - plan.at.lat) < 0.02 && Math.abs(me.lon - plan.at.lon) < 0.02) pts.push(me)
  const b = new ctx.ml.LngLatBounds()
  for (const p of pts) b.extend([p.lon, p.lat])
  ctx.map.fitBounds(b, { padding: { top: 44, bottom: 44, left: 36, right: 36 }, maxZoom: 17, duration })
}

/** Back to the whole loop after a spot's sheet: drop the highlight, frame every pin again. */
export function overview(ctx, plan, me) {
  ctx.map.getSource('focus')?.setData({ type: 'FeatureCollection', features: [] })
  fitPlan(ctx, plan, me, 400)
}

/** Highlight one curb and bring it into view. */
export function focusCurb(ctx, geom, { color, zoom = 17.2, animate = true } = {}) {
  const f = lineFeature(geom, { color })
  ctx.map.getSource('focus').setData({ type: 'FeatureCollection', features: [f] })
  const cs = f.geometry.coordinates
  const mid = cs[Math.floor(cs.length / 2)]
  ctx.map.easeTo({ center: mid, zoom: Math.max(ctx.map.getZoom(), zoom), duration: animate ? 500 : 0 })
}


/** A small map focused on one curb (drive mode, parked screen). */
export async function curbMap(el, curb, { color, me } = {}) {
  const ctx = await createMap(el, { center: curb, zoom: 16.5 })
  focusCurb(ctx, curb.geom, { color, zoom: 16.5, animate: false })
  if (me) {
    marker(ctx, 'pin-me', '<span></span>', me, null, 'You')
    // Close by (driving up to it, walking back to the car): frame both you and the curb.
    if (Math.hypot((me.lat - curb.lat) * 111320, (me.lon - curb.lon) * 92300) < 900) {
      const b = new ctx.ml.LngLatBounds([me.lon, me.lat], [me.lon, me.lat])
      for (const [lat, lon] of decodePolyline(curb.geom)) b.extend([lon, lat])
      ctx.map.fitBounds(b, { padding: 48, maxZoom: 17, duration: 0 })
    }
  }
  return ctx
}

/** The check screen: you, and each curb next to you in its verdict colour, numbered like the list. */
export async function hereMap(el, me, curbs) {
  const ctx = await createMap(el, { center: me, zoom: 18 })
  const tone = { ok: COLORS.go, short: '#ff8a3d', no: '#ff7b6b' }
  ctx.map.getSource('focus').setData({
    type: 'FeatureCollection',
    features: curbs.map((c) => lineFeature(c.geom, { color: tone[c.tone] })),
  })
  curbs.forEach((c, i) => marker(ctx, `pin-n pin-tone-${c.tone}`, `<span>${i + 1}</span>`, c, null, `${i + 1}: ${c.street}`, 'bottom'))
  marker(ctx, 'pin-me', '<span></span>', me, null, 'You')
  const b = new ctx.ml.LngLatBounds([me.lon, me.lat], [me.lon, me.lat])
  for (const c of curbs) b.extend([c.lon, c.lat])
  ctx.map.fitBounds(b, { padding: 50, maxZoom: 18.5, duration: 0 })
  return ctx
}

export function setHeat(ctx, on) {
  ctx.map.setLayoutProperty('heat', 'visibility', on ? 'visible' : 'none')
}
